import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Trans, msg } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  CssBaseline,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useTheme
} from '@mui/material'
import MobileTopNav, { MobileTopNavSpacer } from '../components/mobile/MobileTopNav'
import DesktopNav from '../components/desktop/DesktopNav'
import DesktopToolbar from '../components/desktop/DesktopToolbar'
import useOnlineStatus from '../hooks/useOnlineStatus'
import useLogout from '../hooks/useLogout'
import { useMobileLogout } from '../contexts/mobile-logout-context'
import { NAV_LAYOUT_MARGIN_START } from '../consts/nav-layout-margin'
import { formatDateForList } from '../utils'
import {
  canProvisionDevice,
  getDeviceProvisionMeta,
  isDeviceProvisioned
} from '../offline/device-provision-meta'
import { getProvisionOrganizationIdFromEnv } from '../offline/offline-provision-config'
import { runDeviceProvisioningSync } from '../offline/device-provision-sync'

function readSessionUser() {
  try {
    const raw = localStorage.getItem('adams_user')
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

/**
 * @param {unknown} sessionUser
 * @returns {number[]}
 */
function resolveSelectableOrganizationIds(sessionUser) {
  const initUser = sessionUser?.init?.user ?? sessionUser ?? {}
  const fromList = initUser.organization_ids ?? initUser.organizationIds
  if (Array.isArray(fromList) && fromList.length > 0) {
    return fromList.map((id) => Number(id)).filter((n) => Number.isFinite(n))
  }
  const single = initUser.organization ?? initUser.Organization
  if (single != null && single !== '' && Number(single) !== -1) {
    const n = Number(single)
    if (Number.isFinite(n)) return [n]
  }
  return []
}

function readPersistedSession() {
  try {
    const raw = localStorage.getItem('adams_user')
    const u = raw ? JSON.parse(raw) : null
    return {
      token: u?.token || localStorage.getItem('token') || null,
      init: u?.init || {}
    }
  } catch {
    return {
      token: localStorage.getItem('token') || null,
      init: {}
    }
  }
}

/**
 * Wizard “Prepara dispositivo” per Admin / OrganizationAdmin / MultiOrganizationAdmin.
 * URL: /device-provision
 */
export default function DeviceProvision({ position, isMobile }) {
  const { _ } = useLingui()
  const theme = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const state = location.state || {}
  const persistedSession = useMemo(() => readPersistedSession(), [])
  const token = state?.token || persistedSession.token
  const init = state?.init || persistedSession.init || {}
  const { user } = init

  const toolbarHeight = theme.mixins.toolbar.minHeight
  const primaryButtonSx = {
    background:
      theme.palette.newColors?.cyanGradient || theme.palette.primary.main,
    color: '#fff',
    fontWeight: 600,
    '&:hover': {
      background:
        theme.palette.newColors?.cyanGradient || theme.palette.primary.dark
    },
    '&.Mui-disabled': {
      color: 'rgba(255,255,255,0.7)',
      background: theme.palette.action.disabledBackground
    }
  }
  const headerTitleSx = {
    color:
      theme.palette.mode === 'light'
        ? theme.palette.newColors.gray
        : theme.palette.newColors.lightGray,
    fontWeight: 'bold',
    fontSize: 24
  }

  const isOnline = useOnlineStatus()
  const logout = useLogout()
  const { requestLogout: requestMobileLogout } = useMobileLogout()
  const sessionUser = useMemo(() => readSessionUser(), [])
  const userType = sessionUser?.type ?? sessionUser?.init?.user?.type
  const mayProvision = canProvisionDevice(userType)
  const hasToken = Boolean(token || localStorage.getItem('token'))

  const envOrgId = useMemo(() => getProvisionOrganizationIdFromEnv(), [])
  const selectableOrgIds = useMemo(
    () => resolveSelectableOrganizationIds(sessionUser),
    [sessionUser]
  )

  const [selectedOrgId, setSelectedOrgId] = useState(() => {
    if (envOrgId != null) return envOrgId
    if (selectableOrgIds.length === 1) return selectableOrgIds[0]
    return ''
  })

  const [activeStep, setActiveStep] = useState(0)
  const [syncing, setSyncing] = useState(false)
  const [error, setError] = useState(/** @type {string | null} */ (null))
  const [summary, setSummary] = useState(/** @type {object | null} */ (null))
  const [provisionMeta, setProvisionMeta] = useState(/** @type {object | null} */ (null))

  const configOrgId =
    selectedOrgId === '' || selectedOrgId == null ? null : Number(selectedOrgId)

  const refreshMeta = useCallback(async () => {
    const [meta, provisioned] = await Promise.all([
      getDeviceProvisionMeta(),
      isDeviceProvisioned()
    ])
    setProvisionMeta({ ...meta, isProvisioned: provisioned })
  }, [])

  useEffect(() => {
    refreshMeta().catch(() => {})
  }, [refreshMeta])

  const handleStartSync = async () => {
    if (!isOnline) {
      setError(_(msg`An internet connection is required to prepare this device.`))
      return
    }
    if (!hasToken) {
      setError(_(msg`Please log in online first, then return to this page.`))
      return
    }

    if (selectableOrgIds.length > 1 && configOrgId == null && envOrgId == null) {
      setError(
        _(
          msg`Select an organization for this device, or set REACT_APP_OFFLINE_PROVISION_ORGANIZATION_ID in the build.`
        )
      )
      return
    }

    setSyncing(true)
    setError(null)
    setActiveStep(1)

    const orgIds =
      sessionUser?.organization_ids ?? sessionUser?.init?.user?.organization_ids
    const result = await runDeviceProvisioningSync({
      provisionedByRole: userType,
      provisionedOrganizationIds: Array.isArray(orgIds) ? orgIds : undefined,
      organizationIdForConfig: configOrgId ?? envOrgId ?? null
    })

    setSyncing(false)

    if (!result.success) {
      setError(
        result.reason === 'no_token'
          ? _(msg`Session expired. Log in again and retry.`)
          : _(
              msg`Sync failed. Check your connection and permissions, then try again.`
            )
      )
      setActiveStep(0)
      return
    }

    const stats = result.importStats ?? result.results?.userImportStats
    setSummary({
      loginUsersCount: result.loginUsersCount ?? 0,
      usersOk: result.results?.users,
      configOk: result.results?.config,
      organizationsConfigured: result.results?.organizationsConfigured ?? 0,
      organizationIdForConfig:
        result.results?.organizationIdForConfig ?? configOrgId ?? envOrgId,
      tilesOk: result.results?.tiles,
      importStats: stats
    })
    await refreshMeta()
    setActiveStep(2)
  }

  const handleFinishLogout = async () => {
    if (isMobile) {
      await requestMobileLogout()
      return
    }
    await logout()
  }

  const handleStayLoggedIn = () => {
    navigate('/home', {
      state: { ...state, token, init, isHistoryOpen: false }
    })
  }

  const steps = [
    _(msg`Overview`),
    _(msg`Download offline data`),
    _(msg`Device ready`)
  ]

  const showOrgPicker = selectableOrgIds.length > 1 && envOrgId == null

  const renderAccessMessage = (severity, children, actionLabel, action) => (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        maxWidth: 560
      }}
    >
      <Alert severity={severity} sx={{ mb: 2 }}>
        {children}
      </Alert>
      <Button variant="contained" onClick={action} sx={primaryButtonSx}>
        {actionLabel}
      </Button>
    </Paper>
  )

  const renderPageBody = () => {
    if (!mayProvision) {
      return renderAccessMessage(
        'warning',
        <Trans>
          Only organization administrators can prepare a device for offline use.
        </Trans>,
        _(msg`Back to home`),
        () => navigate('/home', { state: { ...state, token, init } })
      )
    }

    if (!hasToken) {
      return renderAccessMessage(
        'info',
        <Trans>Log in with an administrator account before preparing this device.</Trans>,
        _(msg`Go to login`),
        () => navigate('/')
      )
    }

    return (
      <Box sx={{ maxWidth: 960, width: '100%', mx: 'auto' }}>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          <Trans>
            Prepare each phone or tablet before field use. This setup must be completed
            separately on every device that will be used offline.
          </Trans>
        </Typography>

        {!isOnline && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Trans>You are offline. Connect to the network to download data.</Trans>
          </Alert>
        )}

        {envOrgId != null && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Trans>
              Build config: organization configs limited to organization_id
            </Trans>{' '}
            <strong>{envOrgId}</strong> (REACT_APP_OFFLINE_PROVISION_ORGANIZATION_ID).
          </Alert>
        )}

        {provisionMeta?.isProvisioned && activeStep === 0 && (
          <Alert severity="success" sx={{ mb: 2 }}>
            <Trans>This device was already prepared.</Trans>
            {provisionMeta.deviceProvisionedAt ? (
              <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
                {formatDateForList(provisionMeta.deviceProvisionedAt)}
              </Typography>
            ) : null}
          </Alert>
        )}

        <Stepper
          activeStep={activeStep}
          alternativeLabel={!isMobile}
          sx={{ mb: 3, px: { xs: 0, sm: 2 } }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: theme.shadows[1]
          }}
        >
          {activeStep === 0 && (
            <Box>
              <Typography paragraph color="text.secondary">
                <Trans>
                  This setup will download everything needed to use the app offline on
                  this device.
                </Trans>
              </Typography>
              <Typography paragraph color="text.secondary">
                <Trans>
                  After this setup, users will be able to log in and record crash reports
                  on this device without an internet connection.
                </Trans>
              </Typography>

              {showOrgPicker && (
                <FormControl fullWidth sx={{ mb: 2 }} size="small">
                  <InputLabel id="provision-org-label">
                    <Trans>Organization for this device</Trans>
                  </InputLabel>
                  <Select
                    labelId="provision-org-label"
                    label={_(msg`Organization for this device`)}
                    value={selectedOrgId}
                    onChange={(e) => setSelectedOrgId(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>
                        <Trans>All allowed organizations</Trans>
                      </em>
                    </MenuItem>
                    {selectableOrgIds.map((id) => (
                      <MenuItem key={id} value={id}>
                        {id}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              <Button
                variant="contained"
                disabled={!isOnline || syncing}
                onClick={handleStartSync}
                sx={primaryButtonSx}
              >
                <Trans>Prepare Device</Trans>
              </Button>
            </Box>
          )}

          {activeStep === 1 && (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography fontWeight={500}>
                <Trans>Syncing offline data…</Trans>
              </Typography>
              <LinearProgress sx={{ mt: 3, borderRadius: 1 }} />
            </Box>
          )}

          {activeStep === 2 && summary && (
            <Box>
              <Alert severity="success" sx={{ mb: 3 }}>
                <Trans>Device is ready for operator offline login.</Trans>
              </Alert>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={4}>
                  <Paper
                    variant="outlined"
                    sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}
                  >
                    <Typography variant="h5" fontWeight={700} color="primary">
                      {summary.loginUsersCount}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      <Trans>Offline login users</Trans>
                    </Typography>
                  </Paper>
                </Grid>
                {summary.importStats && (
                  <>
                    <Grid item xs={6} sm={4}>
                      <Paper
                        variant="outlined"
                        sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}
                      >
                        <Typography variant="h5" fontWeight={700} color="primary">
                          {summary.importStats.imported}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          <Trans>Imported from directory</Trans>
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Paper
                        variant="outlined"
                        sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}
                      >
                        <Typography variant="h5" fontWeight={700}>
                          {summary.organizationsConfigured}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          <Trans>Organizations configured</Trans>
                          {summary.organizationIdForConfig != null
                            ? ` (#${summary.organizationIdForConfig})`
                            : ''}
                        </Typography>
                      </Paper>
                    </Grid>
                  </>
                )}
              </Grid>

              {summary.importStats && (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    mb: 2
                  }}
                >
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`${_(msg`Skipped (no verifier)`)}: ${summary.importStats.skippedNoVerifier}`}
                  />
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`${_(msg`Skipped (Argon2 legacy)`)}: ${summary.importStats.skippedArgon2}`}
                  />
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`${_(msg`Skipped (invalid format)`)}: ${summary.importStats.skippedInvalidFormat}`}
                  />
                  <Chip
                    size="small"
                    color={summary.configOk ? 'success' : 'error'}
                    variant="outlined"
                    label={`${_(msg`Config sync`)}: ${summary.configOk ? 'OK' : 'failed'}`}
                  />
                </Box>
              )}

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                <Trans>
                  Log out now so an operator can use offline login. IndexedDB data will
                  not be erased.
                </Trans>
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Button
                  variant="contained"
                  onClick={handleFinishLogout}
                  sx={primaryButtonSx}
                >
                  <Trans>Log out and leave device ready</Trans>
                </Button>
                <Button variant="outlined" onClick={handleStayLoggedIn}>
                  <Trans>Stay logged in</Trans>
                </Button>
              </Box>
            </Box>
          )}
        </Paper>
      </Box>
    )
  }

  return (
    <Box component="main" sx={{ height: `calc(100vh - ${toolbarHeight}px)` }}>
      <CssBaseline />
      {isMobile ? (
        <>
          <MobileTopNav token={token} user={user} />
          <MobileTopNavSpacer />
        </>
      ) : (
        <>
          <DesktopNav position={position} subPanelElements={[]} />
          <DesktopToolbar />
        </>
      )}

      <Box
        display="flex"
        flexDirection="column"
        p={1}
        alignItems="stretch"
        sx={{
          ...(!isMobile && { marginInlineStart: NAV_LAYOUT_MARGIN_START }),
          overflow: 'auto',
          height: isMobile ? 'auto' : `calc(100vh - ${toolbarHeight}px)`
        }}
      >
        <Box
          sx={{
            p: 1,
            borderBottom: `1px solid ${theme.palette.newColors.gray}`
          }}
        >
          <Typography sx={headerTitleSx}>
            <Trans>Prepare device for offline use</Trans>
          </Typography>
        </Box>

        <Box sx={{ p: { xs: 1, sm: 2 }, flex: 1 }}>{renderPageBody()}</Box>
      </Box>
    </Box>
  )
}
